import { Dashboard, QuillProvider } from "@quillsql/react"
import Cookies from "js-cookie"
import { useState } from "react"

import useUser from "@hooks/useUser"

import replaceSlugs from "@utils/replaceSlugs"

import { REPORTS_ENDPOINTS } from "@endpoints/reports"

import styles from "./ReportDashboard.module.scss"
import { getQuillTheme } from "./ReportDashboard.themes"
import { ReportDashboardProps } from "./ReportDashboard.types"
import ReportDetailDialog from "./ReportDetailDialog/ReportDetailDialog"
import ReportEditorDialog from "./ReportEditorDialog/ReportEditorDialog"
import { QuillButton } from "./components/QuillButton/QuillButton"
import QuillDialog from "./components/QuillDialog/QuillDialog"

function ReportsProvider(props: { children: React.ReactNode }) {
    const { children } = props

    const { user } = useUser()
    const { quillTheme } = getQuillTheme()

    if (window.USE_QUILL_CLOUD) {
        return (
            <QuillProvider
                publicKey={window.QUILL_PUBLIC_KEY}
                theme={quillTheme}
                tenants={[{ tenantField: "service_company_id", tenantIds: [user?.service_company.id ?? ""] }]}
            >
                {children}
            </QuillProvider>
        )
    } else {
        const serviceCompanySlug = user?.service_company?.slug ?? "undefined"
        const queryEndpoint = replaceSlugs(REPORTS_ENDPOINTS.DATA, {
            service_company_slug: serviceCompanySlug,
        })

        return (
            <QuillProvider
                publicKey={window.QUILL_PUBLIC_KEY}
                queryEndpoint={queryEndpoint}
                queryHeaders={{
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                }}
                withCredentials={true}
                theme={quillTheme}
            >
                {children}
            </QuillProvider>
        )
    }
}

export default function ReportDashboard(props: ReportDashboardProps) {
    const { dashboardId, isAddReportDialogOpen, onAddReportDialogChange } = props

    const { chartContainerStyle } = getQuillTheme()
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null)

    return (
        <ReportsProvider>
            <div className={styles.base}>
                <Dashboard
                    name={dashboardId}
                    onClickReport={(report) => {
                        setSelectedReportId(report.id)
                    }}
                    ButtonComponent={QuillButton}
                    SecondaryButtonComponent={QuillButton}
                    ModalComponent={QuillDialog}
                    chartContainerStyle={chartContainerStyle}
                />
                {selectedReportId && (
                    <ReportDetailDialog
                        isOpen={!!selectedReportId}
                        onOpenChange={(open) => !open && setSelectedReportId(null)}
                        reportId={selectedReportId}
                        dashboardId={dashboardId}
                    />
                )}
                <ReportEditorDialog
                    isOpen={isAddReportDialogOpen}
                    onOpenChange={onAddReportDialogChange}
                    dashboardId={dashboardId}
                />
            </div>
        </ReportsProvider>
    )
}
